import { enableStaticRendering } from 'mobx-react';
import AppStore from './appStore';
import AuthStore from './authStore';
import BookingsStore from './bookingsStore';
import InvoicingStore from './invoicingStore';
import EventsStore from './eventsStore';
import CoursesStore from './coursesStore';
import BlogStore from './blogStore';
import CommunityStore from './communityStore';
import deliveriesStore from './deliveriesStore';
import FilesStore from './filesStore';
import SupportStore from './supportStore';
import CheckoutStore from './checkoutStore';
import FloorPlanStore from './floorplanStore';
import { isServer } from 'env/ssr/ServerSideRenderManager';
import ContentStore from './contentStore';
import UserStore from './userStore';
import HomeStore from './homeStore';
import { AuthTokens } from '@portal/env/stores/authTokens/authTokens';

enableStaticRendering(isServer());

let store: ReturnType<typeof initializeServerStore> | null = null;
export const getStores = () => store;

function initializeServerStore(host, authTokens: AuthTokens) {
  return {
    appStore: new AppStore({ host, authTokens }),
    authStore: new AuthStore({ host, authTokens }),
    bookingsStore: new BookingsStore({ host, authTokens }),
    invoicingStore: new InvoicingStore({ host, authTokens }),
    eventsStore: new EventsStore({ host, authTokens }),
    coursesStore: new CoursesStore({ host, authTokens }),
    blogStore: new BlogStore({ host, authTokens }),
    communityStore: new CommunityStore({ host, authTokens }),
    deliveriesStore: new deliveriesStore({ host, authTokens }),
    filesStore: new FilesStore({ host, authTokens }),
    supportStore: new SupportStore({ host, authTokens }),
    checkoutStore: new CheckoutStore({ host, authTokens }),
    floorPlanStore: new FloorPlanStore({ host, authTokens }),
    contentStore: new ContentStore({ host, authTokens }),
    userStore: new UserStore({ host, authTokens }),
    homeStore: new HomeStore({ host, authTokens }),
    authTokens,
  } as const;
}

export default function initializeStore({ stores, host, authTokens }) {
  //If running in the server, create new stores
  if (isServer()) {
    return initializeServerStore(host, authTokens);
  }

  //If running in the client, create new stores and hydrate
  //based on the data passed in from the server.
  if (store === null) {
    store = {
      appStore: new AppStore({ data: stores.appStore, authTokens }),
      authStore: new AuthStore({ data: stores.authStore, authTokens }),
      homeStore: new HomeStore({ data: stores.homeStore, authTokens }),
      bookingsStore: new BookingsStore({
        data: stores.bookingsStore,
        authTokens,
      }),
      invoicingStore: new InvoicingStore({
        data: stores.invoicingStore,
        authTokens,
      }),
      eventsStore: new EventsStore({ data: stores.eventsStore, authTokens }),
      coursesStore: new CoursesStore({ data: stores.coursesStore, authTokens }),
      blogStore: new BlogStore({ data: stores.blogStore, authTokens }),
      communityStore: new CommunityStore({
        data: stores.communityStore,
        authTokens,
      }),
      deliveriesStore: new deliveriesStore({
        data: stores.deliveriesStore,
        authTokens,
      }),
      filesStore: new FilesStore({
        data: stores.filesStore,
        authTokens,
      }),
      supportStore: new SupportStore({ data: stores.supportStore, authTokens }),
      checkoutStore: new CheckoutStore({
        data: stores.checkoutStore,
        authTokens,
      }),
      floorPlanStore: new FloorPlanStore({
        data: stores.floorPlanStore,
        authTokens,
      }),
      contentStore: new ContentStore({ data: stores.contentStore, authTokens }),
      userStore: new UserStore({ data: stores.userStore, authTokens }),
      authTokens,
    } as const;
  }

  return store;
}
